import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { videoSort } from '../util/constants';
import moment from 'moment';
import { Box, Text } from 'rebass/styled-components';
import useVideosListener from 'hooks/useVideosListener';
import { motion } from 'framer-motion';
import VideoContainer from './videoComponents/videoContainer';
import { VideoSortBar } from './videoComponents/videoSortBar';
import RowIconButton from './shell/RowIconButton';
import { ChevronIcon, DeleteIcon } from './shell/Icons';

const Bar = styled(Box)`
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
`;

const Container = styled(Box)<{ dropped: boolean }>`
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  /* UI refresh (5 Nov 2026): an open record gets a light grey heading, not charcoal. */
  ${Bar} {
    background-color: ${(p) => (p.dropped ? '#F9FAFB' : '#FFFFFF')};
  }
`;

const recordInput = (size: number, weight: number, color: string): React.CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  height: 34,
  border: '1px solid transparent',
  borderRadius: 8,
  padding: '0 8px',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: size,
  fontWeight: weight,
  color,
  minWidth: 0,
});

const InputContainer = styled.div<{ width: string }>`
  width: ${(p) => p.width};
  min-width: ${(p) => (p.width === '100%' ? '150px' : p.width)};
  box-sizing: border-box;
`;

const DateText = styled(Text)<{ dropped: boolean }>`
  text-align: center;
`;

const Dropdown = styled(motion.div)`
  background-color: ${(p) => p.theme.colors.offWhite};
  display: flex;
  flex-direction: column;
`;

type Props = {
  record: IRecord;
  deleteRecord(): void;
  updateRecord(id: string, record: Partial<IRecord>): void;
};

const Record: React.FC<Props> = ({ record, deleteRecord, updateRecord }) => {
  const [currentSort, setCurrentSort] = useState<{
    sortType: videoSort;
    direction: 'desc' | 'asc';
  }>({ sortType: videoSort.date, direction: 'asc' });

  const [uploading, setUploading] = useState(false);
  const { videos, updateSort } = useVideosListener(record.id);
  const [dropped, setDropped] = useState(false);
  const [keyNumber, setKeyNumber] = useState(record.keyNumber);
  const [title, setTitle] = useState(record.title);

  useEffect(() => {
    if (title && keyNumber) {
      if (record.title !== title) {
        setTitle(record.title);
      }
      if (record.keyNumber !== keyNumber) {
        setKeyNumber(record.keyNumber);
      }
    }
  }, [record]);

  useEffect(() => {
    updateSort(currentSort);
  }, [currentSort]);

  return (
    <>
      <Container
        dropped={dropped}
        sx={{
          // A lighter rounded card inside the rounded Videos & Audio bucket (Aaron, 5 Nov 2026).
          border: '1px solid #E4E6EA',
          borderRadius: '12px',
          overflowY: 'hidden',
          boxShadow: 'none',
        }}
      >
        <Bar className="archy-record-bar" px={[2, 3]} sx={{ height: 60, gap: '10px', borderBottom: dropped ? '1px solid #EEF0F2' : 'none' }}>
          <RowIconButton label={dropped ? 'Hide files' : 'Show files'} disabled={uploading} onClick={() => setDropped((current) => !current)}>
            <ChevronIcon up={dropped} color="#2E333B" />
          </RowIconButton>
          <InputContainer width={'150px'} className="archy-record-key">
            <input
              className="archy-edit"
              aria-label="Key or catalogue number"
              value={keyNumber || ''}
              placeholder="Key / cat no."
              onChange={(e) => setKeyNumber(e.target.value)}
              onBlur={(e) => updateRecord(record.id, { keyNumber: e.target.value })}
              style={recordInput(13, 500, '#5B616E')}
            />
          </InputContainer>
          <InputContainer width={'100%'} className="archy-record-title">
            <input
              className="archy-edit"
              aria-label="Record title"
              value={title || ''}
              placeholder="Record title"
              onChange={(e) => setTitle(e.target.value)}
              onBlur={(e) => updateRecord(record.id, { title: e.target.value })}
              style={recordInput(14, 600, '#16181D')}
            />
          </InputContainer>
          <InputContainer width={'92px'} className="archy-record-date">
            <DateText dropped={dropped} sx={{ fontSize: '13px', color: '#5B616E', whiteSpace: 'nowrap' }}>
              {moment.unix(record.date).format('D MMM YYYY')}
            </DateText>
          </InputContainer>
          <RowIconButton label="Delete record" danger onClick={deleteRecord}>
            <DeleteIcon color="#B3261E" size={16} />
          </RowIconButton>
        </Bar>
        <Dropdown
          animate={dropped ? 'visible' : 'hidden'}
          transition={{ ease: 'easeInOut', duration: 0.25 }}
          variants={{ hidden: { height: 0 }, visible: { height: 400 } }}
        >
          <VideoSortBar
            currentSort={currentSort}
            update={(sortType, direction) => setCurrentSort({ sortType, direction })}
          />
          <VideoContainer files={videos} record={record} isUploading={setUploading} />
        </Dropdown>
      </Container>
    </>
  );
};

export default Record;
